// ─────────────────────────────────────────────────────────────────────────
// Tour scripts — every step of every track lives here. Single source of
// truth: when the UI changes, this file is the one place to update.
//
// Anchors use `data-tour="<scope>.<element>"` attributes added to the JSX
// (so refactors that change class names / structure don't silently break
// the tour). Steps with a missing anchor are skipped silently with a dev
// console.warn (see TourProvider).
//
// Each track also declares which page(s) it runs on, so the help menu
// only offers contextually-relevant tracks (e.g. the "chat" track only
// appears when the user is currently in IncidentChat).
// ─────────────────────────────────────────────────────────────────────────

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';
export type Role          = 'ADMIN' | 'MANAGER' | 'USER';
export type PageKey       = 'dashboard' | 'report' | 'chat' | 'advisor' | 'training' | 'settings' | 'confirmation';

export interface TourStep {
  id:          string;
  anchor:      string;            // data-tour value
  title:       string;
  body:        string;            // plain text or short markdown (** bold ** / *italic* / `code`)
  placement?:  TourPlacement;
  /** If set, only show this step for users whose role matches. */
  roles?:      Role[];
  /** Optional CTA label for the primary action (defaults to "Next"). */
  ctaLabel?:   string;
  /** If set, the engine waits up to N ms for the anchor to appear before skipping. */
  waitFor?:    number;
}

export interface TourTrack {
  id:        string;
  label:     string;
  /** Short blurb shown in the help menu. */
  summary:   string;
  /** Pages this track is available on. If undefined, available everywhere. */
  pages?:    PageKey[];
  /** Roles this track is available to. If undefined, all roles. */
  roles?:    Role[];
  /** Optional emoji/glyph for the help menu icon (legacy fallback). */
  glyph?:    string;
  /** Line-icon name (see components/Icon) for the help menu — preferred over
   *  glyph so every walkthrough row reads as one consistent monochrome set. */
  icon?:     string;
  /** Steps in display order. */
  steps:     TourStep[];
}

// ─────────────────────────────────────────────────────────────────────────
// Track: Dashboard walkthrough — the main "show them everything" tour.
// One script, role-aware steps. Admin sees the full version with the
// admin-only tiles (Staff/Roster/All Incidents); STAFF skips those.
// ─────────────────────────────────────────────────────────────────────────

export const DASHBOARD_TRACK: TourTrack = {
  id:      'dashboard',
  label:   'Dashboard walkthrough',
  summary: 'Tour of everything on your home screen — tiles, alerts, lists.',
  pages:   ['dashboard'],
  glyph:   '🏠',
  icon:    'grid',
  steps: [
    {
      id:        'console',
      anchor:    'dash.console',
      title:     'Your command console',
      body:      'This is the **Dashboard** — your command deck. It reads the current posture, surfaces the live incident, and lists everything in play. Every incident and quick action starts here.',
      placement: 'bottom',
    },
    {
      id:        'report',
      anchor:    'nav.report',
      title:     'Report an incident',
      body:      'Click here whenever something happens that needs a response — a fire, an injury, a cyber alert, anything CRITICAL through MINOR. It opens the report form and kicks off the agent chat.',
      placement: 'right',
    },
    {
      id:        'nav-dashboard',
      anchor:    'nav.dashboard',
      title:     'Navigation',
      body:      'The sidebar switches between your main areas. **Dashboard** is home — the pulse of every incident.',
      placement: 'right',
    },
    {
      id:        'nav-training',
      anchor:    'nav.training',
      title:     'Training',
      body:      'Your assigned training modules, with deadlines and completion tracking, live here.',
      placement: 'right',
    },
    {
      id:        'nav-advisor',
      anchor:    'nav.advisor',
      title:     'Crisis Advisor',
      body:      'Need advice but it\'s NOT a real incident yet? Ask the advisor — it walks you through "what do I do if X happens" without raising anything.',
      placement: 'right',
    },
    {
      id:        'nav-staff',
      anchor:    'nav.staff',
      title:     'Staff & Roster (Admin)',
      body:      'Invite team members, manage profiles, assign CIMT roles, and build the weekly roster the sign-off cascade uses. **Admins and managers only.**',
      placement: 'right',
      roles:     ['ADMIN', 'MANAGER'],
    },
    {
      id:        'glance',
      anchor:    'dash.glance',
      title:     'At-a-glance stats',
      body:      'Total, critical, open, and resolved counts for this period — your quick pulse check.',
      placement: 'bottom',
    },
    {
      id:        'live-command',
      anchor:    'dash.livecommand',
      title:     'Live incident command',
      body:      'The most pressing active incident, in detail — its auto-generated SitRep, acknowledge SLA, and a one-click **Open SitRep** to jump into the workspace.',
      placement: 'top',
    },
    {
      id:        'triage',
      anchor:    'dash.activelist',
      title:     'Triage queue',
      body:      'Every active incident, most severe first. Click any row to open its workspace — chat, SitRep, IAP, and sign-off all in one place.',
      placement: 'top',
    },
    {
      id:        'help',
      anchor:    'nav.help',
      title:     'Walkthroughs live here',
      body:      'Click this **?** anytime to re-run this tour, take another walkthrough (Report flow, Incident chat, Settings), or restart onboarding.',
      placement: 'bottom',
      ctaLabel:  'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track: Report Incident form
// ─────────────────────────────────────────────────────────────────────────

export const REPORT_TRACK: TourTrack = {
  id:      'report',
  label:   'How to report an incident',
  summary: 'Walks through the report form — what each field is for.',
  pages:   ['report'],
  glyph:   '🚨',
  icon:    'alert',
  steps: [
    {
      id:    'intro',
      anchor: 'report.form',
      title: 'Raise an incident',
      body:  'This form opens a new incident, kicks off the chat with the agent, and notifies the CIMT (for CRITICAL/MODERATE). Fill it the best you can — the agent completes the gaps in chat. You don\'t set severity here: the agent assesses it once the chat starts (more reliable than a gut call at intake).',
      placement: 'bottom',
    },
    {
      id:    'title',
      anchor: 'report.title',
      title: 'Incident title',
      body:  'A short headline. Think "Fire — Building C warehouse" or "Cyber breach — payroll system". Front-load the *what*.',
      placement: 'bottom',
    },
    {
      id:    'location',
      anchor: 'report.location',
      title: 'Location',
      body:  'Building, site, or system affected. Used by the stakeholder logic to flag who needs to know.',
      placement: 'bottom',
    },
    {
      id:    'description',
      anchor: 'report.description',
      title: 'Description',
      body:  'A few sentences. **Facts first** — what happened, when, where. Don\'t worry about completeness — the chat agent will ask the rest.',
      placement: 'top',
    },
    {
      id:    'submit',
      anchor: 'report.submit',
      title: 'Submit',
      body:  'Hitting this raises the incident, opens the chat, and (for CRITICAL/MODERATE) starts paging the on-roster CIMT. The next screen lets you jump straight into the SitRep.',
      placement: 'top',
      ctaLabel: 'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track: IncidentChat — the workspace. Runs on whatever incident the user
// is currently viewing; tour describes, never mutates.
// ─────────────────────────────────────────────────────────────────────────

export const CHAT_TRACK: TourTrack = {
  id:      'chat',
  label:   'Running an incident',
  summary: 'Tour of the incident workspace — chat, SitRep, IAP, sign-off.',
  pages:   ['chat'],
  glyph:   '🛡',
  icon:    'clipboard',
  steps: [
    {
      id:    'header',
      anchor: 'chat.header',
      title: 'Incident workspace',
      body:  'This is your single pane of glass — chat with the agent on the left, the SitRep / CIMT / IAP / Timeline tabs on the right. Everything for this incident lives here.',
      placement: 'bottom',
    },
    {
      id:    'chat-input',
      anchor: 'chat.input',
      title: 'Chat with the agent',
      body:  'Tell the agent what\'s happening in plain English. It builds the **SitRep** (facts, severity, casualties, stakeholders) and updates the right pane as you talk. Hit **Enter** to send.',
      placement: 'top',
    },
    {
      id:    'tab-sitrep',
      anchor: 'chat.tab.sitrep',
      title: 'SitRep tab',
      body:  'The Situation Report — facts, assumptions, recommendations, severity ratings, casualties, and impacted stakeholders. Updates live as you chat.',
      placement: 'left',
      waitFor: 900,
    },
    {
      id:    'tab-cimt',
      anchor: 'chat.tab.cimt',
      title: 'CIMT tab',
      body:  'The team rostered to this incident — who\'s on first call, who\'s the alternate, who handles what role.',
      placement: 'left',
      waitFor: 900,
    },
    {
      id:    'tab-iap',
      anchor: 'chat.tab.iap',
      title: 'IAP tab',
      body:  'The **Incident Action Plan** — objectives, strategies, actions, comms, sign-off. Generated by the agent from your SitRep, reviewed by you.',
      placement: 'left',
      waitFor: 900,
    },
    {
      id:    'tab-meetings',
      anchor: 'chat.tab.meeting',
      title: 'Meetings tab',
      body:  'Schedule CIMT meetings, attach Teams links, paste notes — and "Process notes" regenerates the IAP based on what was discussed.',
      placement: 'left',
      waitFor: 900,
    },
    {
      id:    'tab-timeline',
      anchor: 'chat.tab.timeline',
      title: 'Timeline tab',
      body:  'Auto-generated event log — every IAP revision, every sign-off attempt, every meeting. Your audit trail.',
      placement: 'left',
      waitFor: 900,
    },
    {
      id:    'schedule-meeting',
      anchor: 'chat.schedulemeeting',
      title: 'Schedule a CIMT meeting',
      body:  'Sends an Outlook calendar invite to the rostered CIMT with the SitRep + IAP attached. Decline still works — they\'re not silently subscribed.',
      placement: 'bottom',
      // The schedule button lives INSIDE the Meetings panel — the tour driver
      // switches the right panel to it; give the panel time to mount.
      waitFor: 900,
    },
    {
      id:    'view-report',
      anchor: 'chat.viewreport',
      title: 'View HTML report',
      body:  'A polished, tabbed HTML view of the whole incident — same data as the XLSX export but readable in a browser. Send the link to anyone who needs visibility.',
      placement: 'bottom',
    },
    {
      id:    'finish',
      anchor: 'chat.input',
      title: 'You\'re set',
      body:  'That\'s the workspace tour. The agent will guide you through the SitRep → IAP → sign-off in chat. Stay safe.',
      placement: 'top',
      ctaLabel: 'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track: Crisis Advisor
// ─────────────────────────────────────────────────────────────────────────

export const ADVISOR_TRACK: TourTrack = {
  id:      'advisor',
  label:   'Crisis Advisor',
  summary: 'How to use the advisor for "what should I do if X?" questions.',
  pages:   ['advisor'],
  glyph:   '🛟',
  icon:    'message',
  steps: [
    {
      id:    'intro',
      anchor: 'advisor.header',
      title: 'Advisory, not incident-raising',
      body:  'The Crisis Advisor is for **guidance** — practical "what do I do if X happens" answers from Claude. It will never raise, modify, or sign off a real incident.',
      placement: 'bottom',
    },
    {
      id:    'chips',
      anchor: 'advisor.chips',
      title: 'Common scenarios',
      body:  'Click any chip to jump into a common scenario — missing staff, cyber breach, media inquiry, bomb threat, severe weather. Or type your own question below.',
      placement: 'auto',
    },
    {
      id:    'input',
      anchor: 'advisor.input',
      title: 'Ask anything',
      body:  'The advisor replies in structured phases — **Immediate** (next 15 min) → **Escalate** → **Stabilise** → **Follow-up**. Each phase as a colour-coded card.',
      placement: 'top',
      ctaLabel: 'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track: Training Hub
// ─────────────────────────────────────────────────────────────────────────

export const TRAINING_TRACK: TourTrack = {
  id:      'training',
  label:   'Training Hub',
  summary: 'Tour of your assigned modules and how completion works.',
  pages:   ['training'],
  glyph:   '🎓',
  icon:    'cap',
  steps: [
    {
      id:    'intro',
      anchor: 'training.hub',
      title: 'Training Hub',
      body:  'Crisis-management training your admin has assigned to you, with deadlines and completion tracking.',
      placement: 'bottom',
    },
    {
      id:    'list',
      anchor: 'training.list',
      title: 'Your modules',
      body:  'Each card is one module. Cards with a red deadline are overdue. Click into one to start.',
      placement: 'auto',
    },
    {
      id:    'finish',
      anchor: 'training.hub',
      title: 'That\'s training',
      body:  'When you complete a module, your admin sees it on the team dashboard. Compliance reports are auto-generated.',
      placement: 'bottom',
      ctaLabel: 'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track: Settings — admin team setup
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
// Track: Microsoft Teams setup (admin only) — points the admin at the
// new Integrations tab + walks through the form fields.
// ─────────────────────────────────────────────────────────────────────────

export const TEAMS_TRACK: TourTrack = {
  id:      'teams',
  label:   'Set up Microsoft Teams',
  summary: 'Connect your tenant so the CIMT can schedule meetings + pull transcripts.',
  pages:   ['settings'],
  roles:   ['ADMIN', 'MANAGER'],
  glyph:   '📞',
  icon:    'calendar',
  steps: [
    {
      id:        'tab',
      anchor:    'settings.tab.integrations',
      title:     'Integrations tab',
      body:      'External services CIMA connects to. Today: Microsoft Teams. Until it\'s set up, **no incidents can be raised** on this instance.',
      placement: 'bottom',
    },
    {
      id:        'card',
      anchor:    'integrations.teams',
      title:     'Microsoft Teams card',
      body:      'Status, configuration, and the per-permission verification result all live here.',
      placement: 'auto',
    },
    {
      id:        'form',
      anchor:    'integrations.form',
      title:     'The four fields',
      body:      'Tenant ID + App Client ID + App Client Secret + Organiser email. Your Azure admin generates these in the Microsoft 365 admin portal — the **setup guide** linked above walks them through it step-by-step.',
      placement: 'auto',
    },
    {
      id:        'verify',
      anchor:    'integrations.verify_button',
      title:     'Save & verify',
      body:      'On save, CIMA probes every required Graph permission and shows you which (if any) are missing — with a clear hint for how to fix each one in Azure.',
      placement: 'top',
    },
    {
      id:        'disconnect',
      anchor:    'integrations.disconnect_button',
      title:     'Disconnect anytime',
      body:      'You can revoke the integration completely from here — it removes the secret and locks the platform until reconnected.',
      placement: 'top',
      ctaLabel:  'Finish',
    },
  ],
};

export const SETTINGS_TRACK: TourTrack = {
  id:      'settings',
  label:   'Team setup (Admin)',
  summary: 'Walk through staff, invitations, roster, CIMT roles, stakeholders.',
  pages:   ['settings'],
  roles:   ['ADMIN', 'MANAGER'],
  glyph:   '⚙️',
  icon:    'users',
  steps: [
    {
      id:    'intro',
      anchor: 'settings.tabs',
      title: 'Team setup',
      body:  'Everything to get your CIMT live: invite staff, build the weekly roster, configure CIMT roles, and load your stakeholder library.',
      placement: 'bottom',
    },
    {
      id:    'staff',
      anchor: 'settings.tab.staff',
      title: 'Staff list',
      body:  'Every person who can use CIMA. Add, edit, deactivate. Each staff member gets one role (Admin / Manager / User) — gates who can sign off IAPs.',
      placement: 'bottom',
    },
    {
      id:    'invitations',
      anchor: 'settings.tab.invitations',
      title: 'Invitations',
      body:  'Pending invites — track who\'s accepted, resend, or revoke. Invitees get an email with a magic link to set up their account.',
      placement: 'bottom',
    },
    {
      id:    'roster',
      anchor: 'settings.tab.roster',
      title: 'Weekly Roster',
      body:  'Per-week grid of CIMT assignments. For every role, set **Primary**, **Alternate 1**, **Alternate 2**. The cascade is how the system finds who to sign off — Primary first, then fallback.',
      placement: 'bottom',
    },
    {
      id:    'cimt-roles',
      anchor: 'settings.tab.cimtroles',
      title: 'CIMT Roles',
      body:  'The role definitions — Incident Commander, Operations Officer, Communications, HR/Welfare, WHS, Legal, IT/Cyber. You can add custom roles or disable defaults.',
      placement: 'bottom',
    },
    {
      id:    'stakeholders',
      anchor: 'settings.tab.stakeholders',
      title: 'Stakeholders',
      body:  'Your library of internal + external stakeholders. The SitRep auto-flags them as **affected / not affected** based on incident type — and surfaces the reason for each.',
      placement: 'bottom',
      ctaLabel: 'Finish',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// Track registry — order matters for the help menu
// ─────────────────────────────────────────────────────────────────────────

export const ALL_TRACKS: TourTrack[] = [
  DASHBOARD_TRACK,
  REPORT_TRACK,
  CHAT_TRACK,
  ADVISOR_TRACK,
  TRAINING_TRACK,
  SETTINGS_TRACK,
  TEAMS_TRACK,
];

export function findTrack(id: string): TourTrack | null {
  return ALL_TRACKS.find((t) => t.id === id) || null;
}

/**
 * Returns the steps a given role should see — filters out steps with
 * a `roles` array that doesn't include the user's role. Steps without
 * `roles` are visible to everyone.
 */
export function visibleStepsForRole(
  track: TourTrack,
  role:  Role | null | undefined,
): TourStep[] {
  if (!role) return track.steps;
  return track.steps.filter((s) => !s.roles || s.roles.includes(role));
}

/**
 * Returns the tracks available given the user's current page and role.
 * Used by the help menu to surface contextually-relevant tours first.
 */
export function tracksForContext(
  currentPage: PageKey | null | undefined,
  role:        Role | null | undefined,
): TourTrack[] {
  return ALL_TRACKS.filter((t) => {
    if (t.roles && role && !t.roles.includes(role)) return false;
    if (t.pages && currentPage && !t.pages.includes(currentPage)) return false;
    return true;
  });
}
